import { NEW_WORKSHEET_ID } from '@v2/components/base/CellAddressPicker/hooks/useCellAddressValidation';
import { ValidationErrorsMap } from '@v2/states/validation';
import { toJS } from 'mobx';
import { Flowstep, Operation, ProviderType, PullMode } from 'shared/types/flowstep';
import { capitalizeString } from 'shared/utils/utils';
import { makeAutoObservableExt } from '@v2/utils/mobx/make-auto-observable-ext';
import { PullEditorState } from '@v2/components/flowsteps/editors/common/states/pull-editor-state';

export class PullHubspotListState extends PullEditorState {
  constructor() {
    super({ operation: Operation.PULL, provider: ProviderType.HubspotList });
    makeAutoObservableExt(this, PullHubspotListState);
    this.pullSettings.loadObjectTypes();
  }

  get timestampSheetId() {
    return this.pullSettings.createNewSheet ? NEW_WORKSHEET_ID : null;
  }

  get generatedFlowstepName() {
    return `Pull a list ${capitalizeString(this.pullSettings.selectedObject?.label)}`;
  }

  get isFlowstepButtonsDisabled() {
    return !this.pullSettings.selectedObject || this.validationErrors.size > 0;
  }

  get validationErrors() {
    const errors = new Map();
    this.validate(errors);
    return errors;
  }

  validate(errors: ValidationErrorsMap<string>): void {
    this.pullSettings.validate(errors);
  }

  buildFlowstep() {
    return {
      id: this.flowstep?.id,
      flowId: this.flowstep?.flowId,
      name: this.name || this.generatedFlowstepName,
      errorReaction: this.errorReaction,
      operation: Operation.PULL,
      provider: ProviderType.HubspotList,
      version: '4',
      options: {
        ...super.buildFlowstep(),
        target: this.pullOptions.startCell,
        worksheetId: this.worksheetId,
        pullMode: this.pullSettings.pullMode,
        withDateTimeColumn: this.pullSettings.withDateTimeColumn,
        createDataTable: this.pullOptions.createExcelTable,
        useDropdownsForPicklists: this.pullOptions.createDropDowns,
      },
      providerOptions: {
        objectType: this.pullSettings.selectedObjectId,
        objectProps: toJS(this.pullSettings.selectedFieldNames),
        conditions: toJS(this.pullSettings.conditions),
      },
    };
  }

  onFlowstepLoaded(flowstep: Flowstep, meta: { isExact?: boolean; flowstepTarget?: any }) {
    super.onFlowstepLoaded(flowstep, meta);

    const { options = {}, providerOptions = {} } = flowstep;
    const { objectType = '', objectProps = [], conditions = [] } = providerOptions;

    this.pullSettings.createNewSheet = false;
    this.pullSettings.selectedObjectId = objectType;
    this.pullSettings.selectedFieldNames = objectProps;
    this.pullSettings.conditions = conditions;
    this.worksheetId = options.worksheetId;

    this.pullSettings.pullMode = options.pullMode ?? PullMode.OVERWRITE;
    this.pullSettings.withDateTimeColumn = options.withDateTimeColumn ?? true;
    this.pullOptions.startCell = options.target ?? 'A1';
    this.pullOptions.createExcelTable = options.createDataTable ?? true;
    this.pullOptions.createDropDowns = options.useDropdownsForPicklists ?? true;
  }

  isSimilarFlowstep(flowstep: Flowstep): boolean {
    return flowstep.provider === ProviderType.HubspotList && flowstep.operation === Operation.PULL;
  }
}
